import { Box, Card, CardContent, CardHeader } from '@mui/material';

import { NO_DATA_PLACEHOLDER_DASH, Paper, Typography } from '@occc-app/design-system';
import { RetailerNetworkLocationFuelPricesFragment } from '@occc/graphql';

type FuelGradePriceItem = RetailerNetworkLocationFuelPricesFragment['fuelGradePrices'][number];

type FuelPricesCardProps = {
  fuelGradePrices: FuelGradePriceItem[];
};

type FuelPriceTileProps = {
  label: string;
  value: string;
};

const FuelPriceTile = ({ label, value }: FuelPriceTileProps) => (
  <Paper variant='outlined' sx={{ p: 2 }}>
    <Typography variant='body2' color='textSecondary'>
      {label}
    </Typography>
    <Typography variant='h5'>{value}</Typography>
  </Paper>
);

type FuelPriceGroupProps = {
  title: string;
  fuelGradePrices: FuelGradePriceItem[];
  getPrice: (price: FuelGradePriceItem) => string;
};

const FuelPriceGroup = ({ title, fuelGradePrices, getPrice }: FuelPriceGroupProps) => (
  <Box sx={{ '&:not(:last-of-type)': { mb: 3 } }}>
    <Typography variant='subtitle2' gutterBottom>
      {title}
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
      }}
    >
      {fuelGradePrices.map((price, index) => (
        <FuelPriceTile
          key={`${price.description}-${index}`}
          label={price.description}
          value={getPrice(price)}
        />
      ))}
    </Box>
  </Box>
);

export const FuelPricesCard = ({ fuelGradePrices }: FuelPricesCardProps) => {
  if (fuelGradePrices.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader
        title={
          <Typography variant='h6' component='h3'>
            Fuel Prices
          </Typography>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <FuelPriceGroup
          title='Cash Prices'
          fuelGradePrices={fuelGradePrices}
          getPrice={(price) => price.unitPrice?.displayCashPrice ?? NO_DATA_PLACEHOLDER_DASH}
        />
        <FuelPriceGroup
          title='Card Prices'
          fuelGradePrices={fuelGradePrices}
          getPrice={(price) => price.unitPrice?.displayCreditPrice ?? NO_DATA_PLACEHOLDER_DASH}
        />
      </CardContent>
    </Card>
  );
};
